import UpcastWriter from '@ckeditor/ckeditor5-engine/src/view/upcastwriter';
import { message } from 'antd';
import { RcFile } from 'antd/lib/upload/interface';
import { fileToObject } from 'antd/lib/upload/utils';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import {
  AttachmentsAPI,
  FileAttachment,
  FileUploadStatus,
  getUpdatedFileList,
  MAX_FILE_SIZE,
  UploadFileWithAttachment,
} from 'api/attachments';
import { MessageType, UserMessage } from 'api/conversations/types';
import { ErrorCodes } from 'api/errors/types';
import { ProjectId, User } from 'api/models';
import { AttachmentGallery } from 'components/atoms/attachment-gallery';
import { PrivateStyles, withPrivacy } from 'components/atoms/private';
import { RichTextEditor } from 'components/atoms/rich-text-editor';
import { RTE } from 'components/atoms/rte';
import { OnUserMention } from 'components/molecules/mention';
import { toastService } from 'components/molecules/toast';
import { UserMessageReply } from 'components/molecules/user-message-reply';
import { ConversationContext } from 'data/conversation';
import { ClickEventTags, setMarkerForDataDog } from 'hooks/use-data-dog-logger';
import { useExternalIntegrationUserDetails } from 'hooks/use-external-integration-user-details';
import { useGetAiFillsPlugins } from 'hooks/use-get-ai-fills-plugins';
import { useTranslate } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { formatBytes } from 'utils/files';
import { queueMacrotask } from 'utils/macrotask';
import { uid } from 'utils/uid';
import { AiFillMeta } from 'vendor/ckeditor/react-components/ai-fill/types';
import { CommentBoxActionBar } from './action-bar';

const StyledUserMessageReply = styled(UserMessageReply)`
  margin-bottom: 1px;
  box-shadow: var(--shadow-01);
`;

const StyledRTE = styled(RTE)`
  border-top-right-radius: var(--border-radius-small);
  border-top-left-radius: var(--border-radius-small);
`;

const CommentBoxWrapper = withPrivacy(
  styled.div<{ hasReply: boolean }>`
    --ck-spacing-standard: var(--spacing-05);
    --user-message-reply-horizontal-spacing: var(--spacing-05);
    --rte-content-padding: var(--spacing-05);

    border: solid 1px var(--gray-20);
    margin: 0 var(--spacing-05) var(--spacing-05);
    border-top-left-radius: ${({ hasReply }) =>
      !hasReply ? 'var(--border-radius-small)' : '0px'};
    border-top-right-radius: ${({ hasReply }) =>
      !hasReply ? 'var(--border-radius-small)' : '0px'};
    border-bottom-right-radius: var(--border-radius-small);
    border-bottom-left-radius: var(--border-radius-small);
  `,
  PrivateStyles,
);
const MAX_ATTACHMENTS_LIMIT = 10;

export const MessagesComment: FC<{
  actionsOnLeft?: boolean;
  accountId: number;
  projectId: number;
  className?: string;
  placeholder?: string;
  hideSmartfills?: boolean;
  handleUserMention?: OnUserMention;
  reply?: UserMessage;
  messageContent?: string;
  onAddComment(comment: Partial<UserMessage>): void;
  onClearReply(): void;
  disableAttachments?: boolean;
  disableMentions?: boolean;
  hidePrivate?: boolean;
  context?: ConversationContext;
  hasAccessToCreatePrivateCommentsOnly?: boolean;
  readonly?: boolean;
  aiFillMeta?: AiFillMeta;
  enableAiFills?: boolean;
}> = ({
  projectId,
  reply,
  className = '',
  messageContent = '',
  placeholder,
  onAddComment,
  onClearReply,
  handleUserMention,
  disableAttachments = false,
  disableMentions = false,
  hidePrivate = false,
  hasAccessToCreatePrivateCommentsOnly = false,
  actionsOnLeft,
  hideSmartfills,
  readonly,
  aiFillMeta,
  enableAiFills,
}) => {
  const [filesList, setFilesList] = useState<UploadFileWithAttachment[]>([]);
  const [html, setHtml] = useState('');
  const [isPrivate, setIsPrivate] = useState<boolean>(
    hasAccessToCreatePrivateCommentsOnly,
  );
  const [rte, setRte] = useState<RichTextEditor | null>(null);
  const intl = useIntl();
  const latestFileList = useRef<UploadFileWithAttachment[]>(filesList);
  const aiFillPlugins = useGetAiFillsPlugins(enableAiFills);
  const currentUser = useUser();

  const externalOrNativeUserDetails = useExternalIntegrationUserDetails(
    reply?.commentMeta?.externalUser,
    reply?.user as User,
  );
  const translate = useTranslate();

  const hasFileLimitReached = filesList.length >= MAX_ATTACHMENTS_LIMIT;

  useEffect(() => {
    queueMacrotask(() => rte?.focusAtEnd());
  }, [rte, reply]);

  // Update editor content when messageContent changes
  useEffect(() => {
    setHtml(messageContent);
  }, [messageContent]);

  useEffect(() => {
    setIsPrivate(!!reply?.private || hasAccessToCreatePrivateCommentsOnly);
  }, [hasAccessToCreatePrivateCommentsOnly, reply]);

  useEffect(() => {
    latestFileList.current = filesList;
  }, [filesList]);

  useEffect(() => {
    const isUrlText = (data) => {
      if (data?._textData) {
        const url: string = data?._textData;

        return (
          url.toLowerCase().includes('http://') ||
          url.toLowerCase().includes('https://')
        );
      }

      return false;
    };
    const writer = new UpcastWriter(rte?.editor?.editing.view.document);

    const clipboardPipeline: any =
      rte?.editor?.plugins.get('ClipboardPipeline');

    clipboardPipeline?.on('inputTransformation', (evt, data) => {
      if (
        data.content.childCount === 1 &&
        isUrlText(data.content.getChild(0))
      ) {
        const linkUrl = data.content.getChild(0).data;

        // eslint-disable-next-line no-param-reassign
        data.content = writer.createDocumentFragment([
          writer.createElement('a', { href: linkUrl }, [
            writer.createText(linkUrl),
          ]),
        ]);
      }
    });
  }, [rte?.editor?.editing.view.document, rte?.editor?.plugins]);

  const uploadFileRequest = ({ file }) => {
    const updateFile = ({
      attachment,
      status,
    }: {
      attachment?: FileAttachment;
      status: FileUploadStatus;
    }) => {
      const latestFiles = latestFileList.current;
      const fileToUpdate = latestFiles.find((_file) => _file.uid === file.uid);

      if (!fileToUpdate) {
        return;
      }
      const updatedFilesList = getUpdatedFileList(
        fileToUpdate,
        latestFiles,
        attachment,
        status,
      );

      setFilesList(updatedFilesList);
    };

    AttachmentsAPI.attachFiles(file, {
      projectId,
    })
      .then((attachment) => {
        updateFile({
          status: FileUploadStatus.Done,
          attachment,
        });
      })
      .catch(({ response = {} }) => {
        updateFile({
          status: FileUploadStatus.Error,
        });

        const errorData = response?.data || {};
        const [error = {}] = errorData.errors || [];

        if (error.code === ErrorCodes.AttachmentSizeExceeded) {
          toastService({
            title: translate('attachment.error.size.title'),
            message: translate('attachment.add.description'),
          }).error();
        }
      });
  };

  const invalidFileCount = (newFilesList: number): boolean => {
    const totalFilesList = filesList.length + newFilesList;
    const MAX_LIMIT_KEY = 'attachments.add.error.max_limit';

    if (totalFilesList > MAX_ATTACHMENTS_LIMIT) {
      message.error({
        content: intl.formatMessage({
          id: MAX_LIMIT_KEY,
        }),
        key: MAX_LIMIT_KEY,
        maxCount: 1,
      });

      return true;
    }

    return false;
  };

  const invalidFileSize = (file: File): boolean => {
    const MAX_SIZE_KEY = 'attachments.add.error.max_size';

    if (file.size > MAX_FILE_SIZE) {
      message.error({
        content: intl.formatMessage(
          {
            id: MAX_SIZE_KEY,
          },
          {
            formattedSize: formatBytes(MAX_FILE_SIZE),
          },
        ),
        key: MAX_SIZE_KEY,
        maxCount: 1,
      });

      return true;
    }

    return false;
  };

  // we don't need this validation as of now, backend will handle it
  const _beforeUpload = (file, fileList): Promise<void> | boolean => {
    return new Promise((resolve, reject) => {
      if (invalidFileSize(file)) {
        reject(file);
      }

      if (invalidFileCount(fileList.length)) {
        reject(file);
      }

      resolve(file);
    });
  };

  const getAttachments = (): FileAttachment[] => {
    const validFilesList = filesList.filter(
      (file) => file.status === FileUploadStatus.Done,
    );

    return validFilesList.map((file) => file.attachment) as FileAttachment[];
  };

  const hasCommentContent = (): boolean => {
    const attachments = getAttachments();
    const hasComment = (html.length || attachments.length) > 0;

    return hasComment;
  };

  const createComment = () => {
    setMarkerForDataDog(ClickEventTags.CommentPublished);
    const createdAt = new Date().getTime();
    const repliedTo = reply
      ? {
          commentId: reply?.commentId,
          commentThreadId: reply?.commentThreadId,
          createdAt,
          private: reply?.private,
          content: reply?.content,
          user: externalOrNativeUserDetails,
          linkedAttachments: reply.commentMeta?.linkedAttachments,
          commentGuid: reply.commentGuid,
        }
      : undefined;

    if (!hasCommentContent()) {
      return false;
    }

    let linkedAttachments: FileAttachment[] | undefined = getAttachments();

    linkedAttachments =
      linkedAttachments.length > 0 ? linkedAttachments : undefined;

    const newComment: Partial<UserMessage> = {
      messageType: MessageType.UserMessage,
      content: html,
      private: isPrivate,
      commentMeta: {
        repliedTo,
        linkedAttachments,
      },
      user: currentUser.user,
    };

    setHtml('');
    setFilesList([]);

    onAddComment(newComment);

    return true;
  };

  const onFileAddedFromRTE = (files: FileList) => {
    if (invalidFileCount(files.length) || disableAttachments) {
      return true;
    }

    const tempFiles: UploadFileWithAttachment[] = Array.from(files)
      .map((file) => {
        const rcfile = file as RcFile;

        // See https://github.com/ckeditor/ckeditor5-image/pull/254.
        if (!file) {
          return null;
        }

        rcfile.uid = uid();

        let tempFile: UploadFileWithAttachment = rcfile;

        tempFile.status = FileUploadStatus.Uploading;

        tempFile = { ...tempFile, ...fileToObject(rcfile) };

        uploadFileRequest({
          file: rcfile,
        });

        return tempFile;
      })
      .filter(Boolean) as UploadFileWithAttachment[];
    const newFileList: UploadFileWithAttachment[] = [
      ...filesList,
      ...tempFiles,
    ];

    setFilesList(newFileList);

    return true;
  };

  const onFileUpload = ({ file }) => {
    const newFileList = [...latestFileList.current, file];

    setFilesList(newFileList);
    rte?.focus();
  };

  const onRemove = (file: UploadFileWithAttachment) => {
    const index = filesList.indexOf(file);
    const newFileList = [...filesList];

    newFileList.splice(index, 1);

    setFilesList(newFileList);

    if (file.attachment?.attachmentId) {
      AttachmentsAPI.deleteAttachment(file.attachment?.attachmentId);
    }

    return true;
  };

  const onChangeRTEContent = (latest) => setHtml(latest);

  const defaultPlaceholder = intl.formatMessage(
    {
      id: 'conversations.placeholders.send_a_message',
    },
    {
      private: isPrivate,
    },
  );

  return (
    <CommentBoxWrapper
      private={isPrivate}
      className={className}
      hasReply={!!reply}
    >
      {reply && (
        <StyledUserMessageReply
          attachments={reply.commentMeta?.linkedAttachments}
          content={reply.content}
          isPrivate={isPrivate || reply.private}
          replyAuthor={externalOrNativeUserDetails}
          showReplyTo={false}
          onClear={onClearReply}
        />
      )}
      <span data-cy="messages-comment-rte">
        <StyledRTE
          projectId={ProjectId(projectId)}
          handleUserMention={handleUserMention}
          ref={(el) => setRte(el)}
          disallowImages
          disallowMentions={disableMentions}
          data={html}
          placeholder={placeholder || defaultPlaceholder}
          onChange={onChangeRTEContent}
          onEnter={createComment}
          onFileAdded={onFileAddedFromRTE}
          readonly={readonly}
          noTableFormatting
          enableAiFill={aiFillPlugins.length > 0}
          aiFillMeta={aiFillMeta}
        />
      </span>

      <AttachmentGallery
        filesList={filesList}
        onRemoveFile={onRemove}
        isBasicPreview
      />

      <CommentBoxActionBar
        readonly={readonly}
        hideSmartfills={hideSmartfills}
        actionsOnLeft={actionsOnLeft}
        rte={rte}
        projectId={ProjectId(projectId)}
        hideAttachments={disableAttachments}
        disableSendBtn={!hasCommentContent()}
        hasFileLimitReached={hasFileLimitReached}
        hidePrivate={hidePrivate}
        isPrivate={isPrivate}
        isPrivateReply={reply?.private || hasAccessToCreatePrivateCommentsOnly}
        onTogglePrivate={() => setIsPrivate(!isPrivate)}
        onFileUploadRequest={uploadFileRequest}
        onFileUpload={onFileUpload}
        // onFileBeforeUpload={beforeUpload}
        onAddEmoji={(emoji) => rte?.addEmoji(emoji)}
        onSend={createComment}
      />
    </CommentBoxWrapper>
  );
};
